/*
 * Tobari — dynamic cart rewards (cart page only)
 * ------------------------------------------------------------------
 * The cart HTML (rewards ladder, messages, savings) is rendered by
 * snippets/tobari-cart-rewards.liquid. This file only enhances it and
 * re-runs every time tobari.js swaps in a fresh cart:
 *   - converts £-based promo amounts into the shopper's currency
 *   - checks minimum-spend safety nets for EUR / USD shoppers
 *   - animates progress and celebrates newly unlocked rewards
 *   - shows the mobile sticky checkout bar
 *   - fills "Bundle deals" from the Bundles page (/pages/offers)
 *   - quick-add row arrows, daily-deal countdowns
 */
(function () {
  'use strict';
  if (window.__tobariCartRewards) return;
  window.__tobariCartRewards = true;

  var STATE_KEY = 'tbRewards:v1';
  var TEASER_KEY = 'tbBundleTeaser:v2';
  var TEASER_TTL = 10 * 60 * 1000;
  var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var lastState = null;
  var stickyObserver = null;
  var countdownTimer = null;
  var teaserMemo = null;
  var persistTimer = null;

  /* ---------- helpers ---------- */
  function $all(sel, root) { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); }
  function visible(el) { return el && !el.closest('[hidden]'); }
  function firstVisible(sel, root) { return $all(sel, root).filter(visible)[0] || null; }

  function readJSON(storage, key) {
    try { return JSON.parse(window[storage].getItem(key) || 'null'); } catch (e) { return null; }
  }
  function writeJSON(storage, key, value) {
    try { window[storage].setItem(key, JSON.stringify(value)); } catch (e) { /* storage blocked */ }
  }

  function activeCurrency() {
    return (window.Shopify && Shopify.currency && Shopify.currency.active) || 'GBP';
  }
  function currencyRate() {
    var rate = window.Shopify && Shopify.currency && Number(Shopify.currency.rate);
    return rate && rate > 0 ? rate : 1;
  }
  function fromGbp(pence) {
    return activeCurrency() === 'GBP' ? pence : Math.round(pence * currencyRate());
  }
  function formatMoney(cents) {
    var currency = activeCurrency();
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency: currency }).format(Number(cents || 0) / 100);
    } catch (e) {
      return (Number(cents || 0) / 100).toFixed(2) + ' ' + currency;
    }
  }

  var liveRegion = null;
  function announce(text) {
    if (!text) return;
    if (!liveRegion) {
      liveRegion = document.createElement('div');
      liveRegion.className = 'visually-hidden';
      liveRegion.setAttribute('aria-live', 'polite');
      liveRegion.setAttribute('role', 'status');
      document.body.appendChild(liveRegion);
    }
    liveRegion.textContent = '';
    window.setTimeout(function () { liveRegion.textContent = text; }, 60);
  }

  /* ---------- 1. £ promo amounts in the shopper's currency ---------- */
  function convertMoney(root) {
    $all('[data-gbp-money-converted]', root).forEach(function (el) {
      el.textContent = formatMoney(fromGbp(Number(el.getAttribute('data-gbp-pence') || 0)));
    });
  }

  /* ---------- 2. minimum-spend safety net for non-GBP shoppers ----------
     The cautious "short" copy is shown by default; it is only swapped for the
     "unlocked" copy once the converted minimum spend is confirmed here. */
  function hasRate() {
    return !!(window.Shopify && Shopify.currency && Shopify.currency.active && Number(Shopify.currency.rate) > 0);
  }
   function resolveGates(root) {
    if (!hasRate()) return;
    $all('[data-tb-shortfall][data-gate-gbp]', root).forEach(function (el) {
      var need = fromGbp(Number(el.getAttribute('data-gate-gbp') || 0)) - Number(el.getAttribute('data-spend') || 0);
      if (need > 0) el.textContent = formatMoney(need);
    });
    var holder = root.querySelector('[data-tb-scn][data-tb-gates]');
    if (!holder) return;
    // "gate:spend,gate:spend" in ladder order; count how many are met in a row
    var reached = 0;
    var gates = (holder.getAttribute('data-tb-gates') || '').split(',');
    for (var i = 0; i < gates.length; i++) {
      var parts = gates[i].split(':');
      if (Number(parts[1] || 0) >= fromGbp(Number(parts[0] || 0))) reached++;
      else break;
    }
    $all('[data-tb-scn]', root).forEach(function (el) {
      var scn = el.getAttribute('data-tb-scn');
      if (scn !== 'final') el.hidden = scn !== String(reached);
    });
  }

  /* ---------- 3. progress animation + celebration ---------- */
  function sparkle(panel, marker) {
    if (reduceMotion || !marker) return;
    var p = panel.getBoundingClientRect();
    var m = marker.getBoundingClientRect();
    var cx = m.left - p.left + m.width / 2;
    var cy = m.top - p.top + m.height / 2;
    for (var i = 0; i < 7; i++) {
      var s = document.createElement('span');
      var angle = (Math.PI * 2 * i) / 7 + Math.random() * 0.5;
      var dist = 26 + Math.random() * 18;
      s.className = 'tb-rw__burst';
      s.textContent = '✦';
      s.style.left = cx + 'px';
      s.style.top = cy + 'px';
      s.style.setProperty('--dx', Math.round(Math.cos(angle) * dist) + 'px');
      s.style.setProperty('--dy', Math.round(Math.sin(angle) * dist) + 'px');
      s.style.animationDelay = Math.round(Math.random() * 120) + 'ms';
      panel.appendChild(s);
      window.setTimeout(function (node) { node.remove(); }.bind(null, s), 1300);
    }
  }

  // After a hijab is added, bring the rewards panel back into view (below the sticky header)
  // so the shopper sees how close they are to the next reward. Skipped if it's already on screen.
  function revealPanel(panel) {
    var header = document.querySelector('.site-header');
    var offset = (header ? header.getBoundingClientRect().height : 0) + 12;
    var rect = panel.getBoundingClientRect();
    var onScreen = rect.top >= offset - 4 && rect.bottom <= window.innerHeight + 4;
    if (onScreen) return false;
    window.scrollTo({ top: Math.max(0, window.pageYOffset + rect.top - offset), behavior: reduceMotion ? 'auto' : 'smooth' });
    return true;
  }

  function progress(root, isRefresh) {
    var panel = firstVisible('[data-tb-rewards]', root);
    if (!panel) return;
    var token = panel.getAttribute('data-tb-token') || '';
    var n = Number(panel.getAttribute('data-tb-n') || 0);
    var unlocked = (panel.getAttribute('data-tb-unlocked') || '').split(',').filter(Boolean);

    var prev = lastState && lastState.token === token ? lastState : null;
    if (!prev) {
      var stored = readJSON('localStorage', STATE_KEY);
      if (stored && stored.token === token && Date.now() - Number(stored.at || 0) < 12 * 3600 * 1000) prev = stored;
    }
    var prevN = prev ? Number(prev.n || 0) : 0;
    var prevN = prev ? Number(prev.n || 0) : 0;
    var revealed = false;
    if (isRefresh && prev && n > prevN) revealed = revealPanel(panel);
    // Only newly filled cells animate when the cart refreshes in place.
    if (isRefresh) {
      $all('.tb-rw__cell.is-filled', panel).forEach(function (cell) {
        var j = Number(cell.getAttribute('data-tb-cell') || 0);
        if (j <= prevN) cell.classList.add('is-instant');
        else cell.style.setProperty('--i', String(Math.max(0, j - prevN - 1)));
      });
    }

    var prevUnlocked = prev ? (prev.unlocked || []) : [];
    var fresh = unlocked.filter(function (c) { return prevUnlocked.indexOf(c) === -1; });
    var celebrate = panel.getAttribute('data-tb-celebrate') !== 'false';

    if (fresh.length && celebrate) {
      panel.classList.add('is-celebrating');
      var names = [];
      fresh.forEach(function (count) {
        $all('[data-tb-ms="' + count + '"]', panel).forEach(function (el) { el.classList.add('is-pop'); });
        var label = panel.querySelector('.tb-rw__ms[data-tb-ms="' + count + '"] .tb-rw__ms-label');
        if (label) names.push(label.textContent.trim());
        var marker = panel.querySelector('.tb-rw__marker[data-tb-ms="' + count + '"]');
        window.setTimeout(function () { sparkle(panel, marker); }, isRefresh ? 250 : 650);
      });
      var chip = panel.querySelector('.tb-rw__chip');
      if (chip && fresh.indexOf(chip.getAttribute('data-tb-chip')) !== -1) chip.classList.add('is-new');
      if (names.length) announce(names.join(' and ') + ' unlocked');
      window.setTimeout(function () { panel.classList.remove('is-celebrating'); }, 1600);
    }

        lastState = { token: token, n: n, unlocked: unlocked, at: Date.now() };
    root.__tbRevealed = revealed;
    // Persist a moment later: if the page reloads straight away (bundle add, product page
    // redirect), the next page still celebrates what was just unlocked.
    window.clearTimeout(persistTimer);
    persistTimer = window.setTimeout(function () { writeJSON('localStorage', STATE_KEY, lastState); }, 1500);
  }

  /* ---------- 4. sticky checkout bar (mobile) ---------- */
  function setSticky(bar, show) {
    if (!bar) return;
    bar.classList.toggle('is-visible', show);
    bar.setAttribute('aria-hidden', show ? 'false' : 'true');
    var btn = bar.querySelector('button');
    if (btn) btn.tabIndex = show ? 0 : -1;
    document.documentElement.classList.toggle('tb-sticky-on', show);
  }

  function sticky(root) {
    if (stickyObserver) { stickyObserver.disconnect(); stickyObserver = null; }
    var bar = firstVisible('[data-tb-sticky]', root);
    var target = root.querySelector('[data-tb-checkout]');
    if (!bar || !target || !('IntersectionObserver' in window)) {
      document.documentElement.classList.remove('tb-sticky-on');
      return;
    }
    stickyObserver = new IntersectionObserver(function (entries) {
      var entry = entries[entries.length - 1];
      setSticky(bar, !entry.isIntersecting);
    }, { threshold: 0, rootMargin: '0px 0px -20px 0px' });
    stickyObserver.observe(target);
  }

  /* ---------- 5. bundle deals from the Bundles page ---------- */
  function parseOffers(html) {
    var doc = new DOMParser().parseFromString(html, 'text/html');
    return $all('[data-tb-bundle-card]', doc).map(function (card) {
      var content = card.querySelector('.tb-bundle-content') || card;
      var desc = '';
      Array.prototype.some.call(content.children, function (el) {
        if (el.tagName === 'DIV' && !el.className) { desc = el.textContent.replace(/\s+/g, ' ').trim(); return true; }
        return false;
      });
      var paidAttr = card.getAttribute('data-tb-paid-count');
      var paid = paidAttr === null ? null : Number(paidAttr);
      var button = card.querySelector('[data-tb-bundle-add]');
      var selects = $all('select[data-tb-bundle-variant]', card).map(function (select) {
        return $all('option', select).map(function (o) {
          return { v: o.value, t: o.textContent.trim(), s: o.hasAttribute('selected'), d: o.hasAttribute('disabled') };
        });
      });
      var ids = [];
      selects.forEach(function (opts) { opts.forEach(function (o) { ids.push(String(o.v)); }); });
      var images = $all('.tb-bundle-media-item img', card).slice(0, 2).map(function (img) {
        return { src: img.getAttribute('src') || '', srcset: img.getAttribute('srcset') || '', alt: img.getAttribute('alt') || '' };
      });
      var title = ((card.querySelector('h3') || {}).textContent || '').trim();
      var status = ((content.querySelector('.tb-offer-status') || {}).textContent || '').trim();
      var save = ((content.querySelector('.tb-bundle-offer') || {}).textContent || '').replace(/\s+/g, ' ').trim();
      var hasPaid = paid === null ? selects.length > 0 : paid > 0;
      return {
        title: title,
        desc: desc,
        status: status,
        save: save,
        canAdd: !!button && hasPaid && selects.length > 0,
        label: button ? ((button.getAttribute('data-default-label') || '').trim() || button.textContent.trim() || 'Add bundle') : '',
        code: card.getAttribute('data-tb-discount-code') || '',
        selects: selects,
        ids: ids,
        images: images,
        keep: !!title && (!!status || hasPaid)
      };
    }).filter(function (o) { return o.keep; });
  }

  function el(tag, cls, text) {
    var node = document.createElement(tag);
    if (cls) node.className = cls;
    if (text) node.textContent = text;
    return node;
  }

  function teaserCard(o, src) {
    var card = el('article', 'tb-deal tb-deal--teaser' + (o.images.length ? '' : ' tb-deal--noimg'));
    if (o.canAdd) {
      card.setAttribute('data-tb-bundle-card', '');
      if (o.code) card.setAttribute('data-tb-discount-code', o.code);
    }
    var body = el('div', 'tb-deal__body');
    if (o.status) body.appendChild(el('p', 'tb-deal__status', o.status));
    else if (o.save) body.appendChild(el('p', 'tb-deal__perk', o.save));
    body.appendChild(el('h3', 'tb-deal__title', o.title));
    if (o.desc) body.appendChild(el('p', 'tb-deal__text', o.desc));

    var actions = el('div', 'tb-deal__actions');
    if (o.canAdd) {
      var holder = el('div', 'tb-deal__selects');
      holder.hidden = true;
      o.selects.forEach(function (opts) {
        var select = document.createElement('select');
        select.setAttribute('data-tb-bundle-variant', '');
        select.setAttribute('aria-hidden', 'true');
        select.tabIndex = -1;
        opts.forEach(function (opt) {
          var option = document.createElement('option');
          option.value = opt.v;
          option.textContent = opt.t;
          if (opt.s) option.selected = true;
          if (opt.d) option.disabled = true;
          select.appendChild(option);
        });
        holder.appendChild(select);
      });
      body.appendChild(holder);
      var add = el('button', 'btn-dark tb-deal__add', o.label);
      add.type = 'button';
      add.setAttribute('data-tb-bundle-add', '');
      add.setAttribute('data-default-label', o.label);
      actions.appendChild(add);
    }
    var link = el('a', 'tb-deal__link link-underline', o.canAdd ? 'Details' : 'View bundle');
    link.href = src;
    actions.appendChild(link);
    body.appendChild(actions);
    if (o.canAdd) {
      var status = el('p', 'tb-small');
      status.setAttribute('data-tb-card-status', '');
      status.setAttribute('role', 'status');
      status.setAttribute('aria-live', 'polite');
      body.appendChild(status);
    }
    card.appendChild(body);

    if (o.images.length) {
      var media = el('div', 'tb-deal__media tb-deal__media--' + o.images.length);
      o.images.forEach(function (im) {
        var img = document.createElement('img');
        img.src = im.src;
        if (im.srcset) img.srcset = im.srcset;
        img.sizes = '(min-width: 750px) 160px, 30vw';
        img.alt = im.alt;
        img.loading = 'lazy';
        media.appendChild(img);
      });
      card.appendChild(media);
    }
    return card;
  }

  function renderTeaser(box, offers) {
    if (!box || !box.isConnected || !offers) return;
    var skip = (box.getAttribute('data-skip-variants') || ',') + (box.getAttribute('data-in-cart-variants') || ',');
    var limit = Number(box.getAttribute('data-limit') || 3);
    var src = box.getAttribute('data-src') || '/pages/offers';
    var list = offers.filter(function (o) {
      return !o.ids.some(function (id) { return skip.indexOf(',' + id + ',') !== -1; });
    }).slice(0, Math.max(0, limit));
    if (!list.length) return;
    var frag = document.createDocumentFragment();
    var soon = list.every(function (o) { return !o.canAdd; });
    if (soon) frag.appendChild(el('p', 'eyebrow tb-deals__eyebrow', 'Coming soon to Tobari'));
    list.forEach(function (o) { frag.appendChild(teaserCard(o, src)); });
    box.innerHTML = '';
    box.appendChild(frag);
    box.classList.add('is-filled');
  }

  function teaser(root) {
    var box = root.querySelector('[data-tb-bundle-teaser]');
    if (!box || Number(box.getAttribute('data-limit') || 0) <= 0) return;
    var src = box.getAttribute('data-src') || '/pages/offers';
    var cached = teaserMemo || readJSON('sessionStorage', TEASER_KEY);
    if (cached && cached.src === src) {
      renderTeaser(box, cached.offers);
      if (Date.now() - cached.at < TEASER_TTL) return;
    }
    fetch(src, { credentials: 'same-origin', headers: { Accept: 'text/html' } })
      .then(function (r) { if (!r.ok) throw new Error('offers ' + r.status); return r.text(); })
      .then(function (html) {
        teaserMemo = { src: src, at: Date.now(), offers: parseOffers(html) };
        writeJSON('sessionStorage', TEASER_KEY, teaserMemo);
        renderTeaser(document.querySelector('[data-tb-bundle-teaser]'), teaserMemo.offers);
      })
      .catch(function () { /* keep the fallback text */ });
  }

  /* ---------- 6. quick-add row: random order per visit ---------- */
  var QA_SHOW = 8;
  var memSeed = null;
  function visitSeed() {
    var seed = readJSON('sessionStorage', 'tbQaSeed');
    if (typeof seed !== 'number') {
      seed = memSeed || Math.floor(Math.random() * 1e9) + 1;
      writeJSON('sessionStorage', 'tbQaSeed', seed);
    }
    memSeed = seed;
    return seed;
  }
  function rank(seed, id) {
    var h = (2166136261 ^ seed) >>> 0;
    var str = String(id);
    for (var i = 0; i < str.length; i++) { h ^= str.charCodeAt(i); h = Math.imul(h, 16777619) >>> 0; }
    return h;
  }
  function shuffleRail(track) {
    var items = $all('.tb-qa__item[data-tb-pid]', track);
    if (items.length < 2) return;
    var seed = visitSeed();
    items.sort(function (a, b) { return rank(seed, a.getAttribute('data-tb-pid')) - rank(seed, b.getAttribute('data-tb-pid')); });
    items.forEach(function (li, i) { li.hidden = i >= QA_SHOW; track.appendChild(li); });
    track.scrollLeft = 0;
  }

  /* ---------- quick-add row arrows ---------- */
  var railUpdaters = [];
  window.addEventListener('resize', function () { railUpdaters.forEach(function (fn) { fn(); }); }, { passive: true });
  function rails(root) {
    railUpdaters = [];
    $all('[data-tb-quick-add-rail]', root).forEach(function (section) {
      var track = section.querySelector('[data-tb-rail]');
      var prev = section.querySelector('[data-tb-rail-prev]');
      var next = section.querySelector('[data-tb-rail-next]');
            if (!track) return;
      if (track.hasAttribute('data-tb-shuffle')) shuffleRail(track);
      function update() {
        var max = track.scrollWidth - track.clientWidth;
        section.classList.toggle('is-scrollable', max > 4);
        section.classList.toggle('at-start', track.scrollLeft <= 4);
        section.classList.toggle('at-end', track.scrollLeft >= max - 4);
        if (prev) prev.disabled = track.scrollLeft <= 4;
        if (next) next.disabled = track.scrollLeft >= max - 4;
      }
      function go(dir) { track.scrollBy({ left: dir * track.clientWidth * 0.85, behavior: reduceMotion ? 'auto' : 'smooth' }); }
      if (prev) prev.addEventListener('click', function () { go(-1); });
      if (next) next.addEventListener('click', function () { go(1); });
      track.addEventListener('scroll', update, { passive: true });
      railUpdaters.push(update);
      update();
    });
    // "Pick one" links only make sense when the row they point to is on the page.
    $all('[data-tb-scroll]', root).forEach(function (link) {
      var target = document.getElementById((link.getAttribute('href') || '').slice(1));
      if (!target || !visible(target)) link.hidden = true;
    });
  }

  /* ---------- 7. daily-deal countdown (to midnight, London) ---------- */
  function secondsToMidnight(timeZone) {
    try {
      var parts = new Intl.DateTimeFormat('en-GB', { timeZone: timeZone, hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23' }).formatToParts(new Date());
      var t = {};
      parts.forEach(function (p) { t[p.type] = Number(p.value); });
      return 86400 - (t.hour * 3600 + t.minute * 60 + t.second);
    } catch (e) { return -1; }
  }
  function pad(v) { return String(v).padStart(2, '0'); }
  function tickCountdowns() {
    var nodes = $all('[data-tb-deal-countdown]');
    if (!nodes.length) { window.clearInterval(countdownTimer); countdownTimer = null; return; }
    nodes.forEach(function (node) {
      var left = secondsToMidnight(node.getAttribute('data-tz') || 'Europe/London');
      var time = node.querySelector('time');
      if (!time || left < 0) return;
      time.textContent = pad(Math.floor(left / 3600)) + ':' + pad(Math.floor((left % 3600) / 60)) + ':' + pad(left % 60);
    });
  }
  function countdowns(root) {
    if (!root.querySelector('[data-tb-deal-countdown]')) return;
    tickCountdowns();
    if (!countdownTimer) countdownTimer = window.setInterval(tickCountdowns, 1000);
  }

  /* ---------- run on load and after every cart refresh ---------- */
   function enhance(root, isRefresh) {
    if (!root || root.__tbEnhanced) return;
    root.__tbEnhanced = true;
    // The fresh cart has just been swapped in: keep the shopper where they were,
    // unless progress() deliberately scrolls up to show a new reward.
    var savedY = window.pageYOffset;
    try { teaser(root); } catch (e) { console.warn('Tobari rewards: bundles', e); }
    try { convertMoney(root); } catch (e) { console.warn('Tobari rewards: currency', e); }
    try { resolveGates(root); } catch (e) { console.warn('Tobari rewards: gates', e); }
    try { rails(root); } catch (e) { console.warn('Tobari rewards: rail', e); }
    try { sticky(root); } catch (e) { console.warn('Tobari rewards: sticky', e); }
    try { countdowns(root); } catch (e) { console.warn('Tobari rewards: countdown', e); }
    try { progress(root, isRefresh); } catch (e) { console.warn('Tobari rewards: progress', e); }
    if (isRefresh && !root.__tbRevealed && Math.abs(window.pageYOffset - savedY) > 1) window.scrollTo(0, savedY);
  }

  function start() {
    enhance(document.querySelector('[data-tb-cart]'), false);
    new MutationObserver(function (mutations) {
      mutations.forEach(function (m) {
        Array.prototype.forEach.call(m.addedNodes, function (node) {
          if (node.nodeType !== 1) return;
          var root = node.matches('[data-tb-cart]') ? node : node.querySelector('[data-tb-cart]');
          if (root) enhance(root, true);
        });
      });
    }).observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
